import { MouseEventHandler, ReactNode, useMemo, useState } from "react";
import { Undo2 } from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";
import Avatar from "shared/ui/avatar";
import { Button } from "shared/ui/shadcn/button";

import { CampaignCardDefaultI } from "./campaign-card-default";
import { CampaignCardBonusPrice } from "./shared/campaign-card-bonus-price";

export interface CampaignCardHighlightsI extends CampaignCardDefaultI {
  eligibleLeads?: number;
  weeklyActiveCallers?: number;
  bookedMeetings?: number;
  qualifiedMeetings?: number;
  isWeekendEligible?: boolean;
  successfulPitchRecordingURL?: string;

  isFlipEnabled?: boolean;

  upcomingDates?: ReactNode;
  performance?: ReactNode;
  actions?: ReactNode;
}

export const CampaignCardHighlights = ({
  imgSrc,
  title,
  price,
  priceDescription,
  bonusPrice,
  status,
  labels,
  stats,

  isFlipEnabled = true,

  eligibleLeads,
  weeklyActiveCallers,
  bookedMeetings,
  qualifiedMeetings,
  isWeekendEligible,
  successfulPitchRecordingURL,

  onClick,
  className,
}: CampaignCardHighlightsI) => {
  const [isFlippedBackwards, setIsFlippedBackwards] = useState(false);

  const detailsItems = useMemo(
    () =>
      [
        ["Eligible Leads", eligibleLeads || "N/A"],
        ["Weekly Active Callers", weeklyActiveCallers || "N/A"],
        ["Booked Meetings", bookedMeetings || "N/A"],
        ["Qualified Meetings", qualifiedMeetings || "N/A"],
        [
          "No Calling Allowed",
          typeof isWeekendEligible === "boolean" && isWeekendEligible
            ? "Always allowed"
            : "Sat, Sun",
        ],
      ] as const,
    [
      eligibleLeads,
      weeklyActiveCallers,
      bookedMeetings,
      qualifiedMeetings,
      isWeekendEligible,
    ]
  );

  const flipBackwards: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsFlippedBackwards((prev) => !prev);
  };

  return (
    <div className="[perspective:50rem]">
      <div
        className={clsxMerge(
          "card relative flex cursor-pointer flex-col rounded-lg bg-[white] p-6",
          "h-[360px]",
          "perspective-dramatic transition duration-500 [transform-style:preserve-3d]",
          isFlippedBackwards && "[transform:rotateY(180deg)]",
          className
        )}
        onClick={onClick}
      >
        <div className="inset-0 size-full [backface-visibility:hidden]">
          <div className="mb-2 flex w-full justify-between">
            <div>
              <Avatar
                src={imgSrc}
                placeholderText={title}
                className="h-[88px] w-[88px] rounded-lg border-[1px]"
              />
            </div>

            <div className="flex flex-col items-end gap-y-2">
              {status}
              {labels}
            </div>
          </div>

          <article className="flex w-full grow flex-col justify-between">
            <div className="flex grow flex-col">
              <div className="mb-4 flex items-center justify-between">
                <div className="b-typography-h2 line-clamp-1 font-medium">
                  {title}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="ae-typography-h1">{price}</div>
                <CampaignCardBonusPrice className="mt-1" price={bonusPrice} />
              </div>
            </div>

            <section className="mt-3">{stats}</section>

            {priceDescription && <div className="mt-3">{priceDescription}</div>}
          </article>

          {isFlipEnabled && (
            <div className="mt-3 flex justify-center">
              <Button
                variant="ghost"
                className="ae-link w-full"
                onClick={flipBackwards}
              >
                <Undo2 size="16" />
                View Details
              </Button>
            </div>
          )}
        </div>

        {isFlipEnabled && (
          <div
            className={clsxMerge(
              "absolute inset-0 size-full p-6",
              "flex flex-col justify-between",
              "[backface-visibility:hidden] [transform:rotateY(180deg)]"
            )}
          >
            <div className="flex flex-col">
              <h4 className="typography-body-3-bold">Campaign Details</h4>

              <div className="mt-6 flex w-full flex-col gap-4">
                {detailsItems.map(([label, value], idx) => (
                  <div key={idx} className="flex items-center">
                    <span className="typography-body-4-medium w-[220px]">
                      {label}
                    </span>
                    <span className="typography-body-4">{value}</span>
                  </div>
                ))}
              </div>

              {successfulPitchRecordingURL && (
                <div className="mt-5">
                  <h4 className="typography-body-4-bold">
                    Successful Pitch Example
                  </h4>
                  <audio
                    className="mt-2 w-full"
                    controls
                    src={successfulPitchRecordingURL}
                  />
                </div>
              )}
            </div>

            <div className="mt-3 flex w-full justify-center self-end">
              <Button
                variant="ghost"
                className="ae-link w-full"
                onClick={flipBackwards}
              >
                <Undo2 size="16" />
                View Payout
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
